import { useMemo } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import type { ItemStack, TacticExperiment } from "../data/cockpit-data";
import { publishPresentation } from "../data/item-publish-state";
import { useItemStacks, useTacticExperiments } from "../data/queries";
import {
  joinTimelineExperiments,
  metricAvailability,
  tacticExperimentPresentation,
} from "../data/tactic-timeline";
import { badgeVariant, type Tone } from "../format";

function dateLabel(value: string | null): string {
  if (!value) return "date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10);
}

function displayValue(value: string | number | null): string {
  return value === null ? "unavailable" : String(value);
}

function eventTone(eventType: string): Tone {
  if (eventType === "remove") return "critical";
  if (eventType === "change") return "watch";
  return "ok";
}

/**
 * What the machine did to one keyword, over what the keyword then did.
 *
 * Meteoric drew this as a hand-rolled SVG with its own axis arithmetic. It is
 * rewritten onto `ChartFrame` — a module never picks a charting library (I-7),
 * and a bespoke chart in one module is precisely the fragmentation the seam
 * exists to end. The application events survive as reference lines on the same
 * time axis, which is the whole point of the view: the eye has to be able to
 * land on an event and read the metric either side of it.
 *
 * Two honesty rules carry over unchanged. A missing series is NAMED rather than
 * drawn as a flat line at zero, and a trial clock is withheld on an application
 * that never reached Amazon — "under test: 96h" on an unpublished negation
 * measures an experiment that never started.
 */
export function ItemTraceabilityTimeline({
  item,
  experiments,
  experimentState = "ready",
}: {
  item: ItemStack;
  experiments: TacticExperiment[];
  experimentState?: "ready" | "loading" | "error";
}) {
  const joined = useMemo(
    () => joinTimelineExperiments(item.application_timeline, experiments),
    [experiments, item.application_timeline],
  );
  const availability = metricAvailability(item.outcome_series);

  const series = useMemo(
    () =>
      item.outcome_series
        .filter((point) => Number.isFinite(Date.parse(point.observed_at)))
        .sort((left, right) => Date.parse(left.observed_at) - Date.parse(right.observed_at))
        .map((point) => ({
          day: point.observed_at.slice(0, 10),
          profit_yield: point.profit_yield,
          units_sold: point.units_sold,
        })),
    [item.outcome_series],
  );

  // Only events whose day exists on the axis can be placed on it. An event with
  // no matching observation is still listed below — it is simply not drawn at a
  // position the chart cannot honestly give it.
  const eventDays = useMemo(() => {
    const days = new Set(series.map((point) => point.day));
    return joined
      .map((event) => ({ day: dateLabel(event.applied_at), type: event.event_type }))
      .filter((event) => days.has(event.day));
  }, [joined, series]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ks-ink">Item traceability</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {item.keyword} × {item.market}
              {item.asin ? ` × ${item.asin}` : ""}
            </p>
          </div>
        </div>

        {availability.profitYield || availability.unitsSold ? (
          <ChartFrame
            title={`Application events over profit yield and units sold for ${item.keyword}`}
            pointCount={series.length}
            emptyMessage="No dated observations for this item, so there is nothing to chart."
          >
            <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" />
              <YAxis tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" width={48} />
              <Tooltip
                contentStyle={{
                  background: "var(--color-ks-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-ks-sm)",
                  fontSize: 12,
                }}
              />
              {eventDays.map((event, index) => (
                <ReferenceLine
                  key={`${event.day}:${event.type}:${index}`}
                  x={event.day}
                  stroke="var(--color-ks-grey-1)"
                  strokeDasharray="3 4"
                  label={{ value: event.type, position: "top", fontSize: 10 }}
                />
              ))}
              {availability.profitYield && (
                <Line
                  type="monotone"
                  dataKey="profit_yield"
                  name="Profit yield"
                  stroke="var(--color-ks-purple)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {availability.unitsSold && (
                <Line
                  type="monotone"
                  dataKey="units_sold"
                  name="Units sold"
                  stroke="var(--color-ks-grey-1)"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ChartFrame>
        ) : (
          <p className="rounded-lg border border-ks-grey-3 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            Profit-yield and units-sold series unavailable. The application timeline is shown without a metric
            overlay.
          </p>
        )}

        {availability.profitYield && !availability.unitsSold && <UnavailableMetric name="Units-sold" />}
        {!availability.profitYield && availability.unitsSold && <UnavailableMetric name="Profit-yield" />}

        {experimentState === "loading" ? (
          <p className="text-xs text-muted-foreground" role="status">
            Experiment chips are loading…
          </p>
        ) : experimentState === "error" ? (
          <p className="text-xs font-medium text-ks-danger" role="alert">
            Experiment chips are unavailable. Timeline events remain visible.
          </p>
        ) : null}

        {joined.length === 0 ? (
          <p className="text-sm text-muted-foreground">No application events recorded for this item.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {joined.map((event, index) => (
              <li
                key={`${event.application_id}:${event.event_type}:${event.applied_at ?? "unknown"}:${index}`}
                className="rounded-lg border border-ks-grey-3 bg-muted/40 px-3 py-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono text-muted-foreground">{dateLabel(event.applied_at)}</span>
                  <Badge variant={badgeVariant(eventTone(event.event_type))}>{event.event_type}</Badge>
                  <span className="font-semibold text-ks-ink">{event.tactic_name}</span>
                  <Badge variant="outline">
                    {event.tactic_version === null ? "version unavailable" : `v${event.tactic_version}`}
                  </Badge>
                  {event.source && <span className="text-muted-foreground">via {event.source}</span>}
                  <Badge variant="outline" title={publishPresentation(event.publish_state).title}>
                    {publishPresentation(event.publish_state).label}
                  </Badge>
                </div>
                {event.experiments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {event.experiments.map((experiment) => (
                      <ExperimentChip key={experiment.experiment_id} experiment={experiment} />
                    ))}
                  </div>
                )}
                {event.experiments_withheld && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Trial metrics withheld: this application was never published to Amazon, so no test was
                    actually running.
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function ExperimentChip({ experiment }: { experiment: TacticExperiment }) {
  const values = tacticExperimentPresentation(experiment);
  return (
    <Badge variant="outline">
      Under test: {displayValue(values.timeUnderTest)}
      {values.timeUnderTestUnit ? ` ${values.timeUnderTestUnit}` : ""} · Confidence:{" "}
      {displayValue(values.confidence)}
    </Badge>
  );
}

function UnavailableMetric({ name }: { name: string }) {
  return <p className="text-xs text-muted-foreground">{name} series unavailable; no values were inferred.</p>;
}

/** The same view, resolved from an item key — what a draft card has to hand. */
export function ItemTraceabilityByKey({ itemKey }: { itemKey: string }) {
  const stacks = useItemStacks();
  const experiments = useTacticExperiments();
  if (stacks.isPending) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading item traceability…
      </p>
    );
  }
  if (stacks.isError) {
    return (
      <p className="text-sm font-medium text-ks-danger" role="alert">
        Item traceability is unavailable. Retry from the Drafts tab.
      </p>
    );
  }
  const item = stacks.data?.find((row) => row.item_key === itemKey);
  if (!item) return <p className="text-sm text-muted-foreground">Item traceability is not available for this draft.</p>;
  return (
    <ItemTraceabilityTimeline
      item={item}
      experiments={experiments.data ?? []}
      experimentState={experiments.isPending ? "loading" : experiments.isError ? "error" : "ready"}
    />
  );
}
